import React, { useEffect, useState } from 'react';
import { ReactComponent as UserIcon } from 'assets/svg/user.svg';
import TextField from 'views/common/TextField';
import {
  CheckOutlined,
  ExclamationOutlined,
  CopyOutlined,
  MoreOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, Dropdown, Input, message, Select, Spin, Tooltip } from 'antd';
import { useValidate, ValidateContext } from 'views/context/ValidateContext';
import FieldRegister from 'views/common/FieldRegister';
import API from 'api';
import { setUserInfo } from 'store/reducers/auth';
import useEventCallback from 'hooks/useEventCallback';
import Loading from 'views/common/Loading';
import configEnv from 'env';

const InputBlock = (props) => {
  const { value, children } = props;
  return (
    <div className="flex-1 mr-5 d-flex">
      <div
        className={classNames('general-info-block__check', {
          'general-info-block__check--yes': value,
        })}
      >
        {value ? <CheckOutlined /> : <ExclamationOutlined />}
      </div>
      <div className="flex-1">
        {children || <TextField {...props} value={value} />}
      </div>
    </div>
  );
};

const InputCopy = ({ value, label, required, after }) => {
  const [copy, setCopy] = useState(false);
  const onClickCopy = () => {
    navigator.clipboard.writeText(value);
    setCopy(true);
  };

  return (
    <TextField label={label} required={required}>
      <div className="d-flex align-item-center">
        <Input
          addonAfter={
            <div
              onClick={onClickCopy}
              onMouseLeave={() =>
                setTimeout(() => {
                  setCopy(false);
                }, 500)
              }
            >
              <Tooltip title={copy ? 'Copied' : 'Copy'}>
                <CopyOutlined />
              </Tooltip>
            </div>
          }
          disabled
          value={value}
        />
        {after && after}
      </div>
    </TextField>
  );
};

const ApiKeyBlock = ({ apiKey, onRegenerate }) => {
  const onClick = (e) => {
    if (e.key === '1') {
      onRegenerate();
    }
  };

  return (
    <div className="general-info-block">
      <div className="general-info-block__label">API Key</div>
      <div className="general-info-block__content">
        <div className="d-flex pb-5">
          <InputBlock value={apiKey}>
            <div className="api-key">
              <InputCopy
                label="Api Key"
                value={apiKey}
                after={
                  <Dropdown
                    trigger="click"
                    arrow
                    menu={{
                      onClick: onClick,
                      items: [
                        {
                          key: 1,
                          label: 'Regenerate API Key',
                          icon: <EditOutlined />,
                        },
                      ],
                    }}
                  >
                    <Button style={{ marginLeft: '1rem' }} shape="circle">
                      <MoreOutlined />
                    </Button>
                  </Dropdown>
                }
              />
            </div>
          </InputBlock>
        </div>
      </div>
    </div>
  );
};

const transformRequest = (state) => {
  const { fullName, instantMessengerType, instantMessengerUsername, company } =
    state;

  return {
    fullName,
    instantMessengerType,
    instantMessengerUsername,
    company,
  };
};

const transformState = (auth) => {
  const {
    fullName,
    instantMessengerType,
    instantMessengerUsername,
    company,
    userId,
    email,
    avatar,
    businessId,
    apiKey,
  } = auth;

  return {
    fullName,
    instantMessengerType,
    instantMessengerUsername,
    company,
    userId,
    email,
    avatar,
    businessId,
    apiKey,
  };
};

const validateRequired = (field) => (v) => {
  if (!v) {
    return field + 'is required';
  }
};

const GeneralInfo = () => {
  const authInfo = useSelector((state) => state.auth);
  const [infoState, setInfoState] = useState({});
  const validate = useValidate('user-info');
  const dispatch = useDispatch();
  const [uploadImage, setUploadImage] = useState();
  const [previewAvatar, setPreviewAvatar] = useState();
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChangeField = useEventCallback(
    (e, field) => {
      setInfoState((st) => ({ ...st, [field]: e?.target?.value }));
    },
    [infoState]
  );

  const onChangeIntantType = (e) => {
    setInfoState((st) => ({ ...st, instantMessengerType: e }));
  };

  const onClickSave = async () => {
    const error = validate.validateFields();
    if (Object.values(error || {}).length > 0) {
      return message.error('User Info Invalid');
    }

    setLoading(true);
    const body = transformRequest(infoState);
    const res = await API.updateUserInfo(body);
    setLoading(false);

    if (res.status === 200) {
      message.success('Update user info success.');
      return dispatch(setUserInfo({ ...authInfo, ...body }));
    }

    message.error(res.message || 'Update user info fail.');
  };

  const onRegenerate = async () => {
    setLoading(true);
    const res = await API.regenerateKey();
    if (res.status === 200) {
      dispatch(setUserInfo({ ...authInfo, apiKey: res.data.apiKey }));
    }
    setLoading(false);
  };

  const onChangeAvatar = (e) => {
    if (e.target?.files?.[0]) {
      setUploadImage(e.target.files[0]);
      setPreviewAvatar(URL.createObjectURL(e.target.files[0]));
    } else {
      setUploadImage();
      setPreviewAvatar();
    }
  };

  const onSaveAvatar = async () => {
    setLoadingAvatar(true);
    const res = await API.uploadFile('user', uploadImage);

    if (res.status === 200) {
      const { name } = res.data;
      const resUpdate = await API.updateAvatar(name);
      setPreviewAvatar();
      setUploadImage();

      if (resUpdate.status === 200) {
        setLoadingAvatar(false);
        return dispatch(setUserInfo({ ...authInfo, avatar: name }));
      }
    }
    setLoadingAvatar(false);
    message.error('Upload avatar fail.');
  };

  useEffect(() => {
    setInfoState(transformState(authInfo));
    // eslint-disable-next-line
  }, [authInfo]);

  const {
    fullName,
    email,
    userId,
    company,
    instantMessengerType,
    instantMessengerUsername,
    avatar,
    businessId,
  } = infoState;

  return (
    <ValidateContext.Provider value={validate}>
      <div className="gerneral-info-container" spellCheck={false}>
        {loading && <Loading />}
        <div className="general-info-block">
          <div className="general-info-block__label">User Photo</div>
          <div className="general-info-block__content">
            <div className="d-flex">
              <div className="general-info-block__avatar">
                {previewAvatar || avatar ? (
                  <img
                    src={
                      previewAvatar ||
                      `${configEnv.STORAGE_BASE_URL}/user/${avatar}`
                    }
                    alt="user-avatar"
                  />
                ) : (
                  <UserIcon />
                )}
                {!loadingAvatar && (
                  <input
                    onChange={onChangeAvatar}
                    type="file"
                    className="input-avatar"
                    accept=".png,.PNG,.jpeg,.JPEG,.jpg,.JPG"
                  />
                )}
                {previewAvatar && !loadingAvatar && (
                  <Button onClick={onSaveAvatar} className="btn-save">
                    Save
                  </Button>
                )}
                {loadingAvatar && (
                  <div className="loading">
                    <Spin size="small" className="spin-global" />
                  </div>
                )}
              </div>
              <div className="general-info-block__upload-avatar">
                <div className="title">Upload Your Photo</div>
                <div className="description">Upload avatar help us</div>
              </div>
            </div>
          </div>
        </div>
        <ApiKeyBlock apiKey={infoState.apiKey} onRegenerate={onRegenerate} />
        <div className="general-info-block">
          <div className="general-info-block__label">Personal Information</div>
          <div className="general-info-block__content">
            <div className="d-flex pb-5">
              <InputBlock value={businessId}>
                <div className="api-key">
                  <InputCopy required label="Business ID" value={businessId} />
                </div>
              </InputBlock>
            </div>
            <div className="d-flex pb-5">
              <InputBlock value={userId} required label="User ID" disabled>
                <div className="api-key">
                  <InputCopy required label="User ID" value={userId} />
                </div>
              </InputBlock>
            </div>
            <div className="d-flex pb-5 pt-2">
              <InputBlock value={email} required label="Email" disabled />
            </div>
            <div className="d-flex pb-5 pt-2">
              <FieldRegister
                name="fullName"
                value={fullName}
                validate={validateRequired('Fullname')}
                onChange={(e) => onChangeField(e, 'fullName')}
              >
                {({ value, onChange, onBlur, error }) => (
                  <InputBlock
                    value={value}
                    label="Full Name"
                    required
                    onChange={onChange}
                    status={error && 'error'}
                    placeholder="Enter Full Name"
                    onBlur={onBlur}
                  />
                )}
              </FieldRegister>
            </div>
            <div className="d-flex pb-5 pt-2">
              <InputBlock
                value={company}
                placeholder="Enter Company"
                label="Company"
                onChange={(e) => onChangeField(e, 'company')}
              />
            </div>
            <div className="d-flex pb-5 pt-2">
              <InputBlock
                value={instantMessengerType && instantMessengerUsername}
              >
                <TextField label="Instant Messenger">
                  <div className="d-flex intant-messenger">
                    <Select
                      className="flex-1 intant-messenger__select"
                      value={instantMessengerType}
                      onChange={onChangeIntantType}
                      placeholder="Select Type"
                      size="large"
                      style={{ height: '3rem' }}
                    >
                      <Select.Option value="SKYPE">Skype</Select.Option>
                      <Select.Option value="GOOGLE_TALK">
                        Google Talk
                      </Select.Option>
                      <Select.Option value="JABBER">Jabber</Select.Option>
                      <Select.Option value="AIM">AIM</Select.Option>
                    </Select>
                    <div className="flex-1 intant-messenger__text">
                      <TextField
                        value={instantMessengerUsername}
                        onChange={(e) =>
                          onChangeField(e, 'instantMessengerUsername')
                        }
                        placeholder="Enter Messenger Username"
                      />
                    </div>
                  </div>
                </TextField>
              </InputBlock>
            </div>
          </div>
        </div>
        <div className="general-info-block__footer">
          <Button icon={<CheckOutlined />} type="primary" onClick={onClickSave}>
            Save
          </Button>
        </div>
      </div>
    </ValidateContext.Provider>
  );
};

export default GeneralInfo;
